import { useMutation, useQuery } from "@tanstack/react-query";
import { getRfCodeFn } from "../../transactions/payment/payment";
import { CopyIcon } from "../../icons/copyIcon";
import { useEffect, useRef, useState } from "react";
import { useDebounce } from "../../utils/useDebounce";
import { TextField } from "../../components/ui/TextField";
import { SearchIcon } from "../../icons/searchIcon";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { CalendarIcon } from "../../icons/calendarIcon";
import { CalendarContent } from "../../components/invoices/CalendarContent";
import { ListDashesIcon } from "../../icons/listDashesIcon";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/Popover";
import clsx from "clsx";
import { DownFilled } from "../../icons/arrows/downFilled";
import { CSVIcon, ExcelIcon, PDFIcon } from "../../icons/files";
import {
  downloadInvoice,
  getInvoicesFn,
} from "../../transactions/invoices/invoices";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { DownloadFileIcon } from "../../icons/downloadFileIcon";
import { CreditCardIcon } from "../../icons/creditCardIcon";
import moment from "moment";
import {
  exportToCSV,
  exportToExcel,
  generatePdfInvoice,
} from "../../utils/exports";
import Pagination from "../../components/elements/pagination/pagination";
import usePersonStore from "../../store/personStore";
import { FileArrowDown, LeftArrow } from "../../icons";
import { TableSkeletion } from "../../components/elements/skeletions";
import { useOnlinePaymentInitit } from "../../hooks/react-query/usePayment";
import { useToast } from "../../components/ui/Toast/useToast";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { queryClient } from "../../utils/queryClient";
import BannerAlert from "../../components/ui/BannerAlert";

const perPageOptions = [25, 50, 100, "All"];
const exportOptions = [
  { title: "Excel", icon: <ExcelIcon width={20} height={20} />, type: "xls" },
  { title: "CSV", icon: <CSVIcon />, type: "csv" },
  { title: "PDF", icon: <PDFIcon />, type: "pdf" },
];

export const InvoicePage = () => {
  const { isLoyaltyEligible, isLoyaltyEnrolled } = usePersonStore.getState();  
  
  const [rfdata, setRfdata] = useState({ RFCode: "", remainingBalance: null });

  const [popover, setPopover] = useState(false);
  const [popoverMobile, setPopoverMobile] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [popoverExport, setPopoverExport] = useState(false);
  const [popoverExportMobile, setPopoverExportMobile] = useState(false);
  const [customPeriod, setCustomPeriod] = useState({ period: "custom" });
  const [refresh, setRefresh] = useState(0);

  const [asc, setAsc] = useState(true);
  const [displayData, setDisplayData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const [perPage, setPerPage] = useState(10);

  let [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const location = useLocation();
  const bannerRef = useRef();

  const isAdmin = usePersonStore((store) => store.userData.isAdmin);
  const debouncedSearch = useDebounce(search, 300);
  const { toast } = useToast();

  const { data } = useQuery({
    queryKey: ["rfCode"],
    queryFn: getRfCodeFn,
    onSuccess: (response) => {
      setRfdata(response.data);
      if (response.data.remainingBalance === null)
        setRfdata((prevData) => ({ ...prevData, remainingBalance: "0 €" }));
      if (!!response.exceptions && response.exceptions.length > 0) {
        toast({
          variant: "destructive",
          description: response.exceptions[0] || "Προέκυψε κάποιο σφάλμα.",
        });
      }
    },
  });

  const { isFetching, data: invoiceData } = useQuery({
    queryKey: [customPeriod, refresh],
    queryFn: () => getInvoicesFn(customPeriod),
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled: !!customPeriod.month && !!customPeriod.year,
  });

  const { showRetailDashboard } = usePersonStore();

  useEffect(()=>{
    if(isLoyaltyEligible) {
      bannerRef.current?.show("Με την πληρωμή του μισθώματος, κερδίζεις χιλιόμετρα επιβράβευσης Avis More κάθε μήνα!", "bg-[#2E9BFF]");
    } else  bannerRef.current?.hide();
  },[isLoyaltyEligible])

  useEffect(() => {
    let paymentHappened = searchParams.get("paymentStatus");
    if (searchParams && paymentHappened) {
      navigate("/invoices", { state: { paymentHappened: paymentHappened } });
    }
    if (location && location.state) {
      if (location.state.paymentHappened === "success") {
        toast({
          variant: "success",
          description: "Επιτυχημένη Πληρωμή",
        });
      } else if (location.state.paymentHappened === "fail") {
        toast({
          variant: "destructive",
          description:
            "Η πληρωμή σας δεν πραγματοποιήθηκε. Παρακαλώ προσπαθήστε ξανά αργότερα.",
        });
      }
    }
  }, [searchParams, location, navigate, toast]);

  useEffect(() => {
    if (invoiceData) {
      let displayList = [];
      const sortedData = invoiceData.data.invoices.slice().sort((a, b) => {
        const dateA = new Date(a.DocDate);
        const dateB = new Date(b.DocDate);

        if (asc) {
          return dateB - dateA;
        } else {
          return dateA - dateB;
        }
      });
      const copyList =
        debouncedSearch !== ""
          ? sortedData.filter((item) =>
              item.ToVAT.toLowerCase().includes(debouncedSearch.toLowerCase())
            )
          : [...sortedData];

      const startIndex = perPage === "All" ? 0 : (currentPage - 1) * perPage;
      const endIndex =
        perPage === "All" ? invoiceData.count : startIndex + perPage;
      copyList.slice(startIndex, endIndex).map((invoice, index) =>
        displayList.push({
          ΑΦΜ: invoice.ToVAT,
          Όνομα: invoice.FileName,
          "Ημερ/νία": moment(invoice.DocDate).format("DD.MM.YYYY"),
          Αριθμός: invoice.DocNumber,
          Τύπος: invoice.DocType,
          Ποσό: invoice.Amt + " €",
        })
      );

      perPage !== "All" && setTotalPages(Math.ceil(copyList.length / perPage));

      setDisplayData(displayList);
    }
  }, [debouncedSearch, currentPage, invoiceData, perPage, asc, totalPages]);

  useEffect(() => {
    if (invoiceData) {
      setCurrentPage(1);
    }
  }, [debouncedSearch, invoiceData]);

  const mutation = useMutation({
    mutationFn: (invoice) => downloadInvoice(invoice),
  });

  const { mutate: paymentInit } = useOnlinePaymentInitit();

  const handleExport = (type) => {
    const fileName =
      "Τιμολόγια" + customPeriod.month
        ? customPeriod.month + "_" + customPeriod.year
        : "";

    if (type === "csv") {
      exportToCSV(displayData, fileName);
    } else if (type === "xls") {
      exportToExcel(displayData, fileName);
    } else if (type === "pdf") {
      generatePdfInvoice(displayData, "Invoices");
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(data.data.RFCode);
  };

  const handleInvoicePayment = (title) => {
    const item = invoiceData.data.invoices.find(
      (item) => item.FileName === title
    );
    paymentInit(
      {
        DocDate: item.DocDate,
        DocNumber: item.DocNumber,
        DocSeries: item.DocSeries,
        payment_type: "invoice",
      },
      {
        onSuccess: (response) => {
          if (response.exceptions) {
            if (response.exceptions.length === 0) {
              const data = JSON.parse(response.data);
              const url = data.url;
              url && window.location.replace(url);
            } else {
              toast({
                variant: "destructive",
                description: response.exceptions[0],
              });
            }
          }
        },
      }
    );
  };

  return (
    <>
      <BannerAlert ref={bannerRef}/>
      <div
        className="container mx-auto"
        style={{
          height: showRetailDashboard
            ? "calc(100vh - 150px)"
            : "calc(100vh - 76px)",
        }}
      >
        <div className="flex flex-col pt-2 lg:pt-14 w-full items-center justify-center gap-6">
          <div className="w-full flex max-lg:flex-col lg:justify-between lg:items-center">
            <div className="flex max-lg:gap-2">
              <button className="lg:p-2" onClick={() => navigate(-1)}>
                <LeftArrow className={"h-[10px] lg:h-[20px] stroke-black"} />
              </button>
              <h2 className="lg:h3">Τιμολόγια</h2>
            </div>
            <div className="flex max-lg:flex-col lg:gap-3 lg:items-center">
              <div className="w-full justify-between flex items-center">
                <div className="flex flex-col">
                  <div className="flex max-lg:flex-col lg:gap-3 lg:items-center ">
                    <h6 className="max-lg:pt-2 lg:w-5/12 whitespace-nowrap">
                      Κωδικός RF
                    </h6>
                    <div className="w-full justify-between flex items-center">
                      {rfdata.RFCode && (
                        <div className="flex items-center gap-3">
                          <p className="text-body-large">{rfdata.RFCode}</p>
                          <CopyIcon
                            onClick={handleCopy}
                            className={"hover:cursor-pointer"}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex max-lg:flex-col lg:gap-3 lg:items-center">
                    <h6 className="max-lg:pt-2 lg:w-4/12 whitespace-nowrap">
                      Τρέχον Υπόλοιπο
                    </h6>
                    <div className="w-full justify-between flex items-center">
                      {rfdata.remainingBalance && (
                        <div className="flex items-center gap-3">
                          <p className="text-body-large">
                            {rfdata.remainingBalance}
                            {!!rfdata.remainingBalance && " €"}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-6">
                  <Dialog
                    open={popoverMobile}
                    onOpenChange={(open) => setPopoverMobile(open)}
                  >
                    <DialogTrigger>
                      <ListDashesIcon
                        color="white"
                        className={
                          "bg-[#D4002A] rounded-full p-1 h-8 w-8 lg:hidden"
                        }
                      />
                    </DialogTrigger>
                    <DialogContent className="px-6 pt-3 pb-10 flex flex-col gap-2">
                      <h3>Αποτελέσματα ανά σελίδα</h3>
                      {perPageOptions.map((option) => (
                        <div
                          onClick={() => {
                            setPerPage(option);
                            setCurrentPage(1);
                            setPopoverMobile(false);
                          }}
                          className="justify-between rounded-full border border-[#A5A5A5] text-[#282828] px-6 py-3 text-label flex gap-3 items-center"
                        >
                          {option}
                          <ListDashesIcon className={"w-5 h-5"} />
                        </div>
                      ))}
                    </DialogContent>
                  </Dialog>
                  <Dialog
                    open={popoverExportMobile}
                    onOpenChange={(open) => setPopoverExportMobile(open)}
                  >
                    <DialogTrigger
                      disabled={displayData.length === 0}
                      className="disabled:opacity-70"
                    >
                      <FileArrowDown className="stroke-white stroke-1  bg-[#D4002A] rounded-full p-1 h-8 w-8 lg:hidden" />
                    </DialogTrigger>
                    <DialogContent className="px-6 pt-3 pb-10">
                      <h3>Εξαγωγή αρχείου</h3>
                      {exportOptions.map((option) => (
                        <div
                          onClick={() => {
                            handleExport(option.type);
                            setPopoverExportMobile(false);
                          }}
                          className="text-label w-full justify-between rounded-full border border-[#A5A5A5] text-[#282828] px-6 py-3 text-label flex items-center cursor-pointer"
                        >
                          {option.title}
                          {option.icon}
                        </div>
                      ))}
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </div>
          </div>
          <div className="flex lg:gap-6 w-full items-center">
            <div className="w-full w-full lg:max-w-[400px]">
              <TextField
                className={
                  "h-full rounded-full text-body border-none items-center pl-5 py-3 lg:py-4"
                }
                placeholder={"Αναζήτηση"}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                icon={<SearchIcon />}
              />
            </div>
            <Dialog
              open={openModal}
              onOpenChange={(open) => setOpenModal(open)}
            >
              <DialogTrigger>
                <Button
                  className={
                    "max-lg:hidden rounded-full border border-[#A5A5A5] text-[#282828] px-6 py-3 text-label flex gap-3 items-center"
                  }
                >
                  <CalendarIcon className="stroke-black" />
                  {customPeriod.month
                    ? customPeriod.month + "." + customPeriod.year
                    : "Χρονική περίοδος"}
                </Button>
              </DialogTrigger>
              <DialogContent className=" max-lg:p-6 max-lg:gap-0 max-lg:pt-3 max-lg:pb-10 max-lg:h-[100vh]">
                <CalendarContent
                  onSortingClick={(value) => {
                    setAsc(value);
                    setCurrentPage(1);
                  }}
                  sortValue={asc}
                  value={customPeriod}
                  onDismiss={() => {
                    setOpenModal(false);
                  }}
                  onApply={(year, month) => {
                    setCustomPeriod({
                      ...customPeriod,
                      month: month,
                      year: year,
                    });
                    const prevData = queryClient.getQueryData([
                      {
                        ...customPeriod,
                        month: month,
                        year: year,
                      },
                      refresh,
                    ]);
                    if (prevData && prevData.data.invoices.length === 0) {
                      queryClient.invalidateQueries([
                        {
                          ...customPeriod,
                          month: month,
                          year: year,
                        },
                        refresh,
                      ]);
                    }
                    setOpenModal(false);
                  }}
                />
              </DialogContent>
            </Dialog>
            <Popover
              open={popover}
              onOpenChange={(open) => {
                setPopover(open);
              }}
            >
              <PopoverTrigger className="max-lg:hidden rounded-full border border-[#A5A5A5] text-[#282828] px-6 py-3 text-label flex gap-3 items-center">
                <ListDashesIcon />
                <div>
                  <p>Αποτελέσματα</p>
                  <p> ανά σελίδα</p>
                </div>
                {perPage}
              </PopoverTrigger>
              <PopoverContent className="flex flex-col gap-6 w-[250px]">
                {perPageOptions.map((option) => (
                  <div
                    onClick={() => {
                      setPerPage(option);
                      setCurrentPage(1);
                    }}
                    className="justify-center rounded-full border border-[#A5A5A5] text-[#282828] px-6 py-3 text-label flex gap-3 items-center cursor-pointer"
                  >
                    {option}
                  </div>
                ))}
              </PopoverContent>
            </Popover>
            <Popover
              open={popoverExport}
              onOpenChange={(open) => {
                setPopoverExport(open);
              }}
            >
              <PopoverTrigger
                disabled={displayData.length === 0}
                className={clsx(
                  "max-lg:hidden disabled:opacity-70 rounded-full text-white px-6 py-3 text-label flex gap-2 items-center",
                  popoverExport ? "bg-[#282828]" : "bg-[#D4002A]"
                )}
              >
                Εξαγωγή αρχείου
                <DownFilled />
              </PopoverTrigger>
              <PopoverContent className="flex flex-col gap-4 w-[150px]">
                {exportOptions.map((option) => (
                  <div
                    onClick={() => {
                      handleExport(option.type);
                    }}
                    className="text-label w-full justify-between rounded-full border border-[#A5A5A5] text-[#282828] px-6 py-3 text-label flex items-center cursor-pointer"
                  >
                    {option.title}
                    {option.icon}
                  </div>
                ))}
              </PopoverContent>
            </Popover>
            <div className="max-lg:hidden flex gap-2 text-body text-[#151F25] h-fit items-center">
              <p>{asc ? "Νεότερα" : "Παλαιότερα"}</p>
              <div
                onClick={() => {
                  setAsc(!asc);
                  setCurrentPage(1);
                }}
                className=" bg-white rounded-[4px] cursor-pointer"
              >
                <DownFilled
                  className={clsx(
                    "fill-[#595454] h-6 w-6",
                    !asc && "rotate-180"
                  )}
                />
              </div>
            </div>
          </div>
          {isFetching ? (
            <TableSkeletion />
          ) : displayData.length !== 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    {Object.keys(displayData[0]).map((key) => (
                      <TableHead
                        className={clsx(
                          key === "Αριθμός"
                            ? "lg:min-w-[200px]"
                            : "lg:min-w-[150px]"
                        )}
                      >
                        {key}
                      </TableHead>
                    ))}
                    <TableHead>Ενέργειες</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayData.map((invoice, index) => {
                    return (
                      <TableRow key={invoice["Όνομα"]}>
                        {Object.values(invoice).map((value) => (
                          <TableCell key={value}>{value}</TableCell>
                        ))}
                        <TableCell className={"flex gap-2 items-center"}>
                          <DownloadFileIcon
                            onClick={() => {
                              const selectedInvoice =
                                invoiceData.data.invoices.find(
                                  (item) => item.FileName === invoice["Όνομα"]
                                );
                              mutation.mutate(selectedInvoice);
                              setRefresh(refresh + 1);
                            }}
                            className={
                              "h-8 w-8 p-1 cursor-pointer rounded-full border border-[#A5A5A5]"
                            }
                          />
                          {invoiceData &&
                          !isAdmin &&
                          invoiceData.data.invoices.find(
                            (item) => item.FileName === invoice["Όνομα"]
                          ) &&
                          invoiceData.data.invoices.find(
                            (item) => item.FileName === invoice["Όνομα"]
                          ).hasRead ? (
                            <CreditCardIcon
                              onClick={() =>
                                handleInvoicePayment(invoice["Όνομα"])
                              }
                              className={
                                "h-8 w-8 p-1 cursor-pointer rounded-full border border-[#A5A5A5]"
                              }
                            />
                          ) : (
                            <></>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          ) : (
            <p className="text-center pt-10">
              {!customPeriod.year
                ? "Επίλεξε χρονική περίοδο για να εμφανιστούν αποτελέσματα."
                : "Δεν υπάρχουν διαθέσιμα αποτελέσματα. Βεβαιώσου ότι έχεις επιλέξει σωστή χρονική περίοδο. Σε περίπτωση που δεν έχεις κάνει εγγραφή στην Ηλεκτρονική Τιμολόγηση, επικοινώνησε μαζί μας καλώντας στο 801005555 (από σταθερό) ή στο +302106879800 ή μέσω email στο contact@avis.gr."}
            </p>
          )}
        </div>
        {perPage !== "All" && (
          <Pagination
            currentPage={currentPage}
            totalCount={totalPages * perPage}
            pageSize={perPage}
            onPageChange={setCurrentPage}
          />
        )}
      </div>
      <div className="lg:hidden fixed bottom-[50px] flex justify-center w-[100vw]">
        <Button
          onClick={() => {
            setOpenModal(true);
          }}
          className={"bg-white rounded-full h5 px-12 py-3"}
          style={{
            boxShadow: "0px 2px 6px 0px #63636333",
          }}
        >
          Φίλτρα
        </Button>
      </div>
    </>
  );
};
